/**
 * ProfileGeneratorList — saved profile cards (shared library) + "New profile"
 * which opens a template picker. Renders inside the role layout; clicking a
 * card opens the full-screen editor.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from '../../utils/toast';
import { Plus, Search, Pencil, Copy, Trash2, Download, LayoutTemplate, X, FileImage } from 'lucide-react';
import { T } from '../../components/em/tokens';
import pgApi from './pgApi';
import './profileGenerator.css';

import { confirmDialog } from "../../components/em/confirm";
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

function TemplatePicker({ open, onClose, onPick, onBlank }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    pgApi.listTemplates().then(setTemplates).catch(() => {}).finally(() => setLoading(false));
  }, [open]);
  if (!open) return null;
  return (
    <div className="pg-modal-overlay" onClick={onClose}>
      <div className="pg-modal" style={{ width: 'min(760px, 94vw)' }} onClick={(e) => e.stopPropagation()}>
        <div className="pg-modal-head">
          <h3 style={{ font: `700 16px ${T.font}`, color: T.fg1, margin: 0 }}>Start a new profile</h3>
          <button type="button" className="pg-icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="pg-tpl-grid">
          <button type="button" className="pg-tpl-card pg-tpl-blank" onClick={onBlank}>
            <Plus size={26} />
            <span>Blank canvas</span>
          </button>
          {loading && <div style={{ color: T.fg5, font: `500 13px ${T.font}`, padding: 12 }}>Loading templates…</div>}
          {!loading && templates.map((t) => (
            <button key={t.id} type="button" className="pg-tpl-card" onClick={() => onPick(t.id)}>
              <div className="pg-tpl-thumb">
                {t.thumbnail ? <img src={t.thumbnail} alt={t.name} /> : <LayoutTemplate size={24} color={T.fg6} />}
              </div>
              <span className="pg-tpl-name">{t.name}</span>
              {t.category && <span className="pg-tpl-cat">{t.category}</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ProfileGeneratorList() {
  const navigate = useNavigate();
  const location = useLocation();
  const base = location.pathname; // .../profile-generator
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [pickOpen, setPickOpen] = useState(false);

  const load = () => {
    setLoading(true);
    pgApi.listProfiles().then(setProfiles).catch(() => toast.error('Could not load profiles')).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(
    () => profiles.filter((p) => p.name?.toLowerCase().includes(query.trim().toLowerCase())),
    [profiles, query],
  );

  const open = (id) => navigate(`${base}/${id}`);

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!(await confirmDialog('Delete this profile?'))) return;
    try { await pgApi.deleteProfile(id); setProfiles((p) => p.filter((x) => x.id !== id)); }
    catch { toast.error('Delete failed'); }
  };

  const duplicate = async (e, p) => {
    e.stopPropagation();
    try {
      const full = await pgApi.getProfile(p.id);
      await pgApi.createProfile({
        name: `${full.name} copy`, canvas_config: full.canvas_config, layers: full.layers, thumbnail: full.thumbnail,
      });
      load();
    } catch { toast.error('Duplicate failed'); }
  };

  const download = (e, p) => {
    e.stopPropagation();
    if (!p.thumbnail) { toast('Open the profile to export a full-resolution image'); return; }
    const a = document.createElement('a');
    a.href = p.thumbnail; a.download = `${p.name || 'profile'}.png`; a.click();
  };

  return (
    <div className="pg-list">
      <div className="pg-list-head">
        <div>
          <h2 style={{ font: `700 22px ${T.font}`, color: T.fg1, margin: 0 }}>Profile Generator</h2>
          <p style={{ font: `400 13px ${T.font}`, color: T.fg5, margin: '4px 0 0' }}>Compose branded profile cards</p>
        </div>
        <button type="button" className="pg-primary-btn" onClick={() => setPickOpen(true)}>
          <Plus size={16} /> New profile
        </button>
      </div>

      <div className="pg-list-toolbar">
        <div className="pg-search">
          <Search size={15} color={T.fg6} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search profiles…" />
        </div>
        <span style={{ font: `500 12px ${T.font}`, color: T.fg5 }}>{filtered.length} profile{filtered.length === 1 ? '' : 's'}</span>
      </div>

      {loading ? (
        <div style={{ color: T.fg5, font: `500 14px ${T.font}`, padding: 40 }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="pg-empty">
          <FileImage size={40} color={T.fg6} />
          <p style={{ font: `600 15px ${T.font}`, color: T.fg2, margin: '12px 0 4px' }}>No profiles yet</p>
          <p style={{ font: `400 13px ${T.font}`, color: T.fg5, margin: 0 }}>Create your first branded profile card.</p>
          <button type="button" className="pg-primary-btn" style={{ marginTop: 16 }} onClick={() => setPickOpen(true)}>
            <Plus size={16} /> New profile
          </button>
        </div>
      ) : (
        <div className="pg-card-grid">
          {filtered.map((p) => (
            <div key={p.id} className="pg-card" onClick={() => open(p.id)}>
              <div className="pg-card-thumb">
                {p.thumbnail
                  ? <img src={p.thumbnail} alt={p.name} />
                  : <div className="pg-card-thumb-empty"><FileImage size={28} color={T.fg6} /></div>}
                <div className="pg-card-overlay">
                  <button type="button" className="pg-round-btn" title="Edit" onClick={(e) => { e.stopPropagation(); open(p.id); }}><Pencil size={16} /></button>
                  <button type="button" className="pg-round-btn" title="Duplicate" onClick={(e) => duplicate(e, p)}><Copy size={16} /></button>
                  <button type="button" className="pg-round-btn" title="Download" onClick={(e) => download(e, p)}><Download size={16} /></button>
                  <button type="button" className="pg-round-btn pg-danger" title="Delete" onClick={(e) => remove(e, p.id)}><Trash2 size={16} /></button>
                </div>
              </div>
              <div className="pg-card-meta">
                <div className="pg-card-name">{p.name}</div>
                <div className="pg-card-sub">{p.updated_by_name || p.created_by_name || '—'} · {fmtDate(p.updated_at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <TemplatePicker
        open={pickOpen}
        onClose={() => setPickOpen(false)}
        onPick={(tid) => navigate(`${base}/new?templateId=${tid}`)}
        onBlank={() => navigate(`${base}/new`)}
      />
    </div>
  );
}
